import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { useContext } from "react";
import { UserContext, UserProvider } from "./contexts/UserContext";
import { LoadingProvider } from "./contexts/LoadingContext";
import { BadgeProvider } from "./contexts/BadgeContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import LoadingAcorn from "./components/LoadingAcorn";
import App from "./components/App";
import Unauthorized from "./components/Unauthorized";
import SignUp from "./components/SignUp";
// import AcornMain from "./components/Acorn/Acorn";
import Login from "./components/Login";
import StoreEditor from "./components/Store/Store";
import ProductEditor from "./components/Store/ProductEditor";
import OrganizationEditor from "./components/Organization/Organization";
import AcornMain from "./components/Acorn/Acorn";
import AdminDashboard from "./components/Dashboards/AdminDashboard";
import BadgesPage from "./pages/BadgesPage";
import CouponBookPage from "./pages/CouponBookPage";
import AdminTenantsPage from "./pages/AdminTenantsPage";
import HouseKioskTeacherPage from "./pages/HouseKioskTeacherPage";
import HouseKioskMascotPage from "./pages/HouseKioskMascotPage";
import HouseKioskLeaderboardPage from "./pages/HouseKioskLeaderboardPage";
import HouseKioskKioskPage from "./pages/HouseKioskKioskPage";
import HouseKioskRoutes from "./pages/HouseKioskRoutes";

// Helper to detect Google sign-in related cookies
const hasGoogleSignInCookie = (): boolean => {
    if (typeof document === 'undefined') return false;
    const names = document.cookie.split(';').map(c => c.trim().split('=')[0]);
    return names.some(n => n === 'G_ENABLED_IDPS' || n === 'g_state' || n.startsWith('G_AUTHUSER'));
};

const AuthGateRedirect = () => {
    const location = useLocation();
    const path = location.pathname;
    const currentUrl = window.location.origin + location.pathname + location.search;
    const apiBaseUrl = (process.env.API_URL || process.env.VITE_API_URL) ?? '';

    // Respect intentional logout flag to keep users off auto-redirect loop
    try {
        if (sessionStorage.getItem('acorn:recentLogout')) {
            sessionStorage.removeItem('acorn:recentLogout');
            return <Navigate to="/login" replace />;
        }
    } catch {}

    let prefersGoogle = false;
    try { prefersGoogle = (localStorage.getItem('acorn:preferredAuth') || '').toLowerCase() === 'google'; } catch {}
    const envPreferGoogle = String((process.env.VITE_PREFER_GOOGLE_ON_401 as any) ?? '').toLowerCase() === 'true'
        || String((import.meta as any)?.env?.VITE_PREFER_GOOGLE_ON_401 ?? '').toLowerCase() === 'true';
    const isLikelyGoogleAudience = path.startsWith('/shop') || path.startsWith('/student') || path.startsWith('/acorn');

    if (prefersGoogle || hasGoogleSignInCookie() || envPreferGoogle || isLikelyGoogleAudience) {
        // Kick off Google OAuth with return to the original route
        window.location.href = `${apiBaseUrl}/api/account/signingoogle?returnUrl=${encodeURIComponent(currentUrl)}`;
        return null;
    }
    // Otherwise, send to the in-app login
    return <Navigate to="/login" replace />;
};

const ProtectedRoute = ({ children }: { children: JSX.Element }) => {
    const { userProfile, isLoading, isInitialized } = useContext(UserContext);
    const location = useLocation();

    // Still waiting for the initial load from the server/local storage token
    if (!isInitialized || isLoading) {
        return <LoadingAcorn />;
    }

    // If we've completed initialization but there's no user, smart-redirect based on route
    if (!userProfile) {
        // Don't auto-redirect from the home page; but ProtectedRoute won't be used there.
        if (location.pathname === '/' || location.pathname === '') {
            return <Navigate to="/" replace />;
        }
        return <AuthGateRedirect />;
    }

    // If we do have a profile, render the protected component
    return children;
};

const BadgesRoute = () => {
    const { userProfile, hasClaim } = useContext(UserContext);
    const role = (userProfile?.mainRole || '').toUpperCase();
    const isTeacherLike = role.includes('TEACHER') || role.includes('ACORNLEAD');

    // Teachers with the manage-badges claim should land on the real badge editor inside the teacher portal.
    if (isTeacherLike) {
        if (!hasClaim('CanManageBadges')) {
            return <Unauthorized />;
        }
        return <App />;
    }

    // Non-teacher roles (e.g., students) keep the existing badges experience.
    return <BadgesPage />;
};

function MainRouter() {
    return (
        <LoadingProvider>
            <Router>
                <UserProvider>
                    <BadgeProvider>
                        <NotificationProvider>
                            <Routes>
                                <Route path="/" element={<App />} />
                                {/* <Route path="/" element={<ProtectedRoute><StudentProfile /></ProtectedRoute>} /> */}
                                <Route path="/login" element={<Login />} />
                                {/* Normalize accidental path usage like /couponbook&IncludeHeader */}
                                <Route path="/couponbook&IncludeHeader" element={<Navigate to="/couponbook?IncludeHeader" replace />} />
                                <Route path="/couponbook&includeHeader" element={<Navigate to="/couponbook?IncludeHeader" replace />} />
                                <Route path="/couponbook&IncludeHeader=:value" element={<Navigate to="/couponbook?IncludeHeader" replace />} />
                                <Route path="/couponbook&includeHeader=:value" element={<Navigate to="/couponbook?IncludeHeader" replace />} />
                                <Route path="/stores" element={<ProtectedRoute><StoreEditor /></ProtectedRoute>} />
                                <Route path="/store/:storeId/products" element={<ProtectedRoute><ProductEditor /></ProtectedRoute>} />
                                <Route path="/shop/:storeSlug" element={<ProtectedRoute><App /></ProtectedRoute>} />
                                <Route path="/student/jobs" element={<ProtectedRoute><App /></ProtectedRoute>} />
                                <Route path="/student/bank" element={<ProtectedRoute><App /></ProtectedRoute>} />
                                <Route path="/student/badges" element={<ProtectedRoute><App /></ProtectedRoute>} />
                                <Route path="/student/hallpass" element={<ProtectedRoute><App /></ProtectedRoute>} />
                                <Route path="/hallpass" element={<ProtectedRoute><App /></ProtectedRoute>} />
                                <Route path="/housekiosk" element={<ProtectedRoute><HouseKioskRoutes /></ProtectedRoute>}>
                                    <Route path="teacher" element={<HouseKioskTeacherPage />} />
                                    <Route path="mascot" element={<HouseKioskMascotPage />} />
                                    <Route path="mascot/:houseId" element={<HouseKioskMascotPage />} />
                                    <Route path="leaderboard" element={<HouseKioskLeaderboardPage />} />
                                    <Route path="kiosk" element={<HouseKioskKioskPage />} />
                                </Route>
                                {/* Teacher dashboard related pushState paths */}
                                <Route path="/dashboard" element={<ProtectedRoute><App /></ProtectedRoute>} />
                                <Route path="/shop" element={<ProtectedRoute><App /></ProtectedRoute>} />
                                <Route path="/jobs" element={<ProtectedRoute><App /></ProtectedRoute>} />
                                <Route path="/user-management" element={<ProtectedRoute><App /></ProtectedRoute>} />
                                <Route path="/coupons" element={<ProtectedRoute><App /></ProtectedRoute>} />
                                <Route path="/reissue-coupons" element={<ProtectedRoute><App /></ProtectedRoute>} />
                                <Route path="/create-summons" element={<ProtectedRoute><App /></ProtectedRoute>} />
                                <Route path="/media-storage" element={<ProtectedRoute><App /></ProtectedRoute>} />
                                <Route path="/unauthorized" element={<Unauthorized />} />
                                <Route path="/signup" element={<SignUp />} />
                                <Route path="/organization" element={<ProtectedRoute><OrganizationEditor /></ProtectedRoute>} />
                                <Route path="/acorn" element={<ProtectedRoute><AcornMain /></ProtectedRoute>} />
                                <Route path="/admin/stores" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                                <Route path="/admin/hallpass-types" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                                <Route path="/admin/badges" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                                <Route path="/admin/tenants" element={<ProtectedRoute><AdminTenantsPage /></ProtectedRoute>} />
                                <Route path="/badges" element={<ProtectedRoute><BadgesRoute /></ProtectedRoute>} />
                                <Route path="/couponbook" element={<ProtectedRoute><CouponBookPage /></ProtectedRoute>} />
                                <Route path="*" element={<Navigate to="/" />} />
                            </Routes>
                        </NotificationProvider>
                    </BadgeProvider>
                </UserProvider>
            </Router>
        </LoadingProvider>
    );
}

export default MainRouter;
